import React, { useEffect, useState } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import awsExports from './aws-exports';
import QueryServers from './QueryServers';
import './App.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CHART_COLORS = [
  'rgba(27, 58, 107, 0.8)',   // azul SIIMED
  'rgba(122, 193, 67, 0.8)',  // verde SIIMED
  'rgba(209, 50, 18, 0.8)',   // rojo
  'rgba(255, 159, 64, 0.8)',  // naranja
  'rgba(75, 192, 192, 0.8)',  // teal
  'rgba(153, 102, 255, 0.8)', // morado
  'rgba(255, 205, 86, 0.8)',  // amarillo
  'rgba(54, 162, 235, 0.8)',  // azul claro
  'rgba(201, 203, 207, 0.8)', // gris
  'rgba(255, 99, 132, 0.8)',  // rosa
];

Amplify.configure(awsExports);

const API_BASE = 'https://zy6iuduzbj.execute-api.us-east-1.amazonaws.com/StageLambdaPortalAPI';

const FUNCTION_CONFIG = {
  'Activa_Entorno': {
    label: 'Activar Entorno',
    description: 'Inicia los servidores del entorno de trabajo',
    icon: '\u25B6',
    btnClass: 'btn btn-start',
  },
  'Apaga_Entorno': {
    label: 'Apagar Entorno',
    description: 'Detiene los servidores del entorno de trabajo',
    icon: '\u23F9',
    btnClass: 'btn btn-stop',
  },
  'ConsultaEC2': {
    label: 'Estado Servidores',
    description: 'Consulta el estado actual de las instancias EC2',
    icon: '\u2139',
    btnClass: 'btn btn-info',
  },
  'ConsultaWorkSpaces': {
    label: 'WorkSpaces',
    description: 'Estado y actividad de escritorios remotos',
    icon: '\uD83D\uDCBB',
    btnClass: 'btn btn-info',
  },
  'ConsultaArchivos': {
    label: 'Archivos',
    description: 'Descarga backups de clientes desde S3',
    icon: '\uD83D\uDCC2',
    btnClass: 'btn btn-info',
  },
  'ConsultaSetup': {
    label: 'Setup',
    description: 'Consulta y descarga builds del instalador',
    icon: '\uD83D\uDCE6',
    btnClass: 'btn btn-info',
  },
  'GenerarSetup': {
    label: 'Generar Setup',
    description: 'Lanza la generacion del instalador bajo demanda',
    icon: '\u26A1',
    btnClass: 'btn btn-start',
  },
  'GestionSkyOne': {
    label: 'Gestion SkyOne',
    description: 'Administracion de usuarios Autosky',
    icon: '\u2601',
    btnClass: 'btn btn-info',
  },
  'ConsultaServidores': {
    label: 'Consultas sobre Servidores',
    description: 'Ejecuta consultas de lectura sobre las bases de datos de clientes',
    icon: '\ud83d\uddc3',
    btnClass: 'btn btn-info',
  },
};

const PAGE_SIZE = 10;
const IIS_ARCHIVOS_URL = 'http://192.168.1.148/';

// Funciones que son "permisos modificadores" (activan botones en otras cards)
// y no deben aparecer como tarjeta propia en la grilla del portal.
const HIDDEN_FUNCTIONS = new Set(['GestionSetup', 'GestionServidores']);

const SKYONE_FIELD_LABELS = {
  nome: 'Nombre',
  sobrenome: 'Apellido',
  email: 'Email',
  username: 'Usuario',
  password: 'Contraseña',
  fullname: 'Nombre completo',
  description: 'Descripción',
  ativo: 'Activo',
  external_ad: 'AD Externo',
  send_activation_email: 'Enviar correo de activación',
  usr_uuid: 'UUID',
  cli_uuid: 'UUID Cliente',
  clients: 'Clientes',
  amb_uuid: 'UUID Ambiente',
  grupo_seguranca: 'Grupo de seguridad',
  email_admin: 'Email administrador',
  in_maintenance: 'En mantenimiento',
  active_sessions_count: 'Sesiones activas',
  observations: 'Observaciones',
  external_client_code: 'Código externo',
};
const skyLabel = (key) => SKYONE_FIELD_LABELS[key] || key;

function App() {
  const [user, setUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [instances, setInstances] = useState(null);
  const [wsData, setWsData] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [filePath, setFilePath] = useState([]);
  const [setupData, setSetupData] = useState(null);
  const [setupPath, setSetupPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // SkyOne state
  const [skyClients, setSkyClients] = useState(null);
  const [skySelectedClient, setSkySelectedClient] = useState(null);
  const [skyView, setSkyView] = useState('');
  const [skyModal, setSkyModal] = useState(null);
  const [skyFormData, setSkyFormData] = useState({});
  const [qsOpen, setQsOpen] = useState(false);
  const [setupModal, setSetupModal] = useState(null);
  const [setupModalInput, setSetupModalInput] = useState('');
  const [skyExcludeSiimed, setSkyExcludeSiimed] = useState(false);
  const [skyExporting, setSkyExporting] = useState(false);

  // Grid search & pagination state
  const [gridSearch, setGridSearch] = useState({});
  const [gridPage, setGridPage] = useState({});

  const gridSearchFor = (key) => gridSearch[key] || '';
  const gridPageFor = (key) => gridPage[key] || 1;
  const setGridSearchFor = (key, val) => { setGridSearch(prev => ({...prev, [key]: val})); setGridPage(prev => ({...prev, [key]: 1})); };
  const setGridPageFor = (key, val) => setGridPage(prev => ({...prev, [key]: val}));

  const filterAndPaginate = (items, gridKey, extractTexts) => {
    const search = gridSearchFor(gridKey).toLowerCase();
    const filtered = search ? items.filter(item => extractTexts(item).some(t => (t || '').toLowerCase().includes(search))) : items;
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(gridPageFor(gridKey), totalPages);
    const startIdx = (currentPage - 1) * PAGE_SIZE;
    return { filtered, pageItems: filtered.slice(startIdx, startIdx + PAGE_SIZE), currentPage, totalPages };
  };

  const renderGridControls = (gridKey, totalItems, currentPage, totalPages) => (
    <div className="grid-controls">
      <input type="text" className="grid-search" placeholder="Buscar..." value={gridSearchFor(gridKey)} onChange={e => setGridSearchFor(gridKey, e.target.value)} />
      <div className="pagination">
        <button className="btn-page" disabled={currentPage === 1} onClick={() => setGridPageFor(gridKey, currentPage - 1)}>Anterior</button>
        <span className="page-info">Pagina {currentPage} de {totalPages} ({totalItems} registros)</span>
        <button className="btn-page" disabled={currentPage === totalPages} onClick={() => setGridPageFor(gridKey, currentPage + 1)}>Siguiente</button>
      </div>
    </div>
  );

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async (u) => {
        setUser(u);
        try {
          const session = await Auth.currentSession();
          const claims = session.getIdToken().payload;
          const groups = claims['cognito:groups'] || [];
          setUserGroups(Array.isArray(groups) ? groups : []);
        } catch (e) {
          setUserGroups([]);
        }
      })
      .catch(() => Auth.federatedSignIn());
  }, []);

  const getToken = async () => {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  };

  // Cada opcion del portal pinta su propio panel. Al cambiar de opcion hay que
  // cerrar todos los demas, o quedan varios montados a la vez.
  const clearPanels = () => {
    setInstances(null);
    setWsData(null);
    setFileData(null);
    setFilePath([]);
    setSetupData(null);
    setSetupPath([]);
    setSkyClients(null);
    setSkySelectedClient(null);
    setSkyView('');
    setSkyModal(null);
    setQsOpen(false);
  };

  const fetchFunctions = async () => {
    const token = await getToken();
    const response = await fetch(`${API_BASE}/functions`, {
      headers: { Authorization: token }
    });
    const data = await response.json();
    setFunctions(data.functions || []);
    clearPanels();
    setMessage(null);
  };

  useEffect(() => {
    if (user) fetchFunctions();
  }, [user]); // eslint-disable-line

  const executeFunction = async (functionName, extraBody, isInternalNav = false) => {
    setLoading(true);
    setMessage(null);
    if (!isInternalNav) {
      clearPanels();
    }

    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/execute`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ functionName, ...extraBody })
      });
      const data = await response.json();

      if (response.ok) {
        if (data.instances) setInstances(data.instances);
        if (data.workspaces) {
          setWsData(data);
          setGridSearchFor('ws', '');
          setGridSearchFor('wsHistory', '');
        }
        if ((data.folders || data.dates || data.files) && functionName === 'ConsultaArchivos') setFileData(data);
        if (data.setupDiario !== undefined || data.actualizadores !== undefined || (data.files && functionName === 'ConsultaSetup')) setSetupData(data);
        setMessage({ type: 'success', text: data.message || 'Ejecutado correctamente' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al ejecutar' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de conexion: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const browseFolder = (folder) => {
    setFilePath([folder]);
    executeFunction('ConsultaArchivos', { action: 'list-dates', folder: folder.name }, true);
  };

  const browseDate = (date) => {
    const folder = filePath[0];
    setFilePath([folder, date]);
    executeFunction('ConsultaArchivos', { action: 'list-files', folder: folder.name, date: date.name }, true);
  };

  const goBackToFolders = () => {
    setFilePath([]);
    executeFunction('ConsultaArchivos', { action: 'list-folders' }, true);
  };

  const goBackToDates = () => {
    const folder = filePath[0];
    setFilePath([folder]);
    executeFunction('ConsultaArchivos', { action: 'list-dates', folder: folder.name }, true);
  };

  // Setup navigation
  const browseBuild = (build, source) => {
    setSetupPath([{ ...build, source }]);
    executeFunction('ConsultaSetup', { action: 'list-files', buildId: build.name, source }, true);
  };

  const goBackToBuilds = () => {
    setSetupPath([]);
    executeFunction('ConsultaSetup', { action: 'list-builds' }, true);
  };

  const downloadSetupFile = async (fileKey, fileName) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/execute`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ functionName: 'ConsultaSetup', action: 'get-download-url', key: fileKey })
      });
      const data = await response.json();
      if (data.downloadUrl) {
        const link = document.createElement('a');
        link.href = data.downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al obtener URL de descarga' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de descarga: ' + err.message });
    }
  };

  // ─── GestionSetup: borrar archivo, borrar build, subir archivo ───
  // El clic del boton solo abre el modal; la accion real la hace el modal al confirmar.
  const openDeleteFileModal = (fileKey, fileName) => {
    setSetupModal({ type: 'delete-file', fileKey, fileName });
  };
  const openDeleteBuildModal = (build, source) => {
    setSetupModalInput('');
    setSetupModal({ type: 'delete-build', build, source });
  };

  const confirmDeleteSetupFile = async () => {
    if (!setupModal || setupModal.type !== 'delete-file') return;
    const { fileKey } = setupModal;
    setSetupModal(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/execute`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ functionName: 'GestionSetup', action: 'delete-file', key: fileKey })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || 'Archivo eliminado' });
        if (setupPath.length > 0) {
          const b = setupPath[0];
          executeFunction('ConsultaSetup', { action: 'list-files', buildId: b.name, source: b.source }, true);
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al eliminar archivo' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de conexion: ' + err.message });
    }
  };

  const confirmDeleteSetupBuild = async () => {
    if (!setupModal || setupModal.type !== 'delete-build') return;
    const { build, source } = setupModal;
    if (setupModalInput.trim() !== build.name) {
      setMessage({ type: 'error', text: 'El nombre no coincide. Eliminacion cancelada.' });
      return;
    }
    setSetupModal(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/execute`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ functionName: 'GestionSetup', action: 'delete-build', buildId: build.name, source })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || 'Build eliminado' });
        executeFunction('ConsultaSetup', { action: 'list-builds' }, true);
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al eliminar build' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de conexion: ' + err.message });
    }
  };

  const uploadSetupFile = async (build, source, file) => {
    if (!file) return;
    try {
      const token = await getToken();
      // 1) Pedir presigned URL al backend
      const urlRes = await fetch(`${API_BASE}/execute`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          functionName: 'GestionSetup', action: 'get-upload-url',
          buildId: build.name, source, filename: file.name,
        })
      });
      const urlData = await urlRes.json();
      if (!urlRes.ok || !urlData.uploadUrl) {
        setMessage({ type: 'error', text: urlData.error || 'No se pudo obtener URL de upload' });
        return;
      }
      // 2) PUT directo a S3
      setMessage({ type: 'success', text: `Subiendo "${file.name}" (${formatSize(file.size)})...` });
      const putRes = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        body: file,
      });
      if (!putRes.ok) {
        setMessage({ type: 'error', text: `Error subiendo archivo: HTTP ${putRes.status}` });
        return;
      }
      setMessage({ type: 'success', text: `Archivo "${file.name}" subido correctamente` });
      // 3) Refrescar
      executeFunction('ConsultaSetup', { action: 'list-files', buildId: build.name, source }, true);
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de upload: ' + err.message });
    }
  };

  const downloadFile = async (fileKey, fileName) => {
    try {
      const token = await getToken();
      const folder = filePath[0];
      const response = await fetch(`${API_BASE}/execute`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ functionName: 'ConsultaArchivos', action: 'get-download-url', folder: folder.name, key: fileKey })
      });
      const data = await response.json();
      if (data.downloadUrl) {
        const link = document.createElement('a');
        link.href = data.downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al obtener URL de descarga' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de descarga: ' + err.message });
    }
  };

  // -----------------------------------------------------------------------
  // SkyOne helpers
  // -----------------------------------------------------------------------
  const skyoneAction = async (action, extraBody = {}) => {
    setLoading(true);
    setMessage(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE}/execute`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ functionName: 'GestionSkyOne', action, ...extraBody })
      });
      const data = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: data.error || data.message || 'Error en SkyOne' });
        return null;
      }
      return data;
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de conexion: ' + err.message });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const skyLoadClients = async () => {
    clearPanels();
    setSkyView('clients');
    const data = await skyoneAction('list-clients');
    if (data) setSkyClients(data.clients);
  };

  const skySelectClient = async (cli_uuid) => {
    const data = await skyoneAction('get-client', { cli_uuid });
    if (data) {
      setSkySelectedClient(data.client);
      setSkyView('detail');
    }
  };

  const skyGetUserDetail = async (usr_uuid) => {
    const data = await skyoneAction('get-user', { usr_uuid });
    if (data && data.skyoneUser) {
      setSkyModal({ type: 'view', user: data.skyoneUser });
    }
  };

  const skyCreateUser = async () => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (skyFormData.email && !emailRegex.test(skyFormData.email)) {
      setMessage({ type: 'error', text: 'El formato del email no es valido' });
      return;
    }
    const data = await skyoneAction('create-user', { userData: skyFormData });
    if (data) {
      setMessage({ type: 'success', text: 'Usuario creado exitosamente' });
      setSkyModal(null);
      if (skySelectedClient) skySelectClient(skySelectedClient.cli_uuid);
    }
  };

  const skyEditUser = async (usr_uuid) => {
    const data = await skyoneAction('edit-user', { usr_uuid, userData: skyFormData });
    if (data) {
      setMessage({ type: 'success', text: 'Usuario editado exitosamente' });
      setSkyModal(null);
      if (skySelectedClient) skySelectClient(skySelectedClient.cli_uuid);
    }
  };

  const skyExportUsers = async (scope) => {
    if (skyExporting) return;
    setSkyExporting(true);
    try {
      const extra = { excludeSiimedDomain: skyExcludeSiimed };
      const action = scope === 'client' ? 'export-users-client' : 'export-users-all';
      if (scope === 'client') {
        if (!skySelectedClient) return;
        extra.cli_uuid = skySelectedClient.cli_uuid;
      }
      const data = await skyoneAction(action, extra);
      if (!data || !data.xlsxBase64) return;
      const binStr = atob(data.xlsxBase64);
      const bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename || 'skyone-usuarios.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `Exportados ${data.rowCount} usuarios` });
    } finally {
      setSkyExporting(false);
    }
  };

  const skyToggleUser = async (usr_uuid, currentAtivo, sendActivationEmail = false) => {
    const payload = { usr_uuid, ativo: !currentAtivo };
    if (!currentAtivo && sendActivationEmail && skySelectedClient) {
      payload.send_activation_email = true;
      payload.cli_uuid = skySelectedClient.cli_uuid;
    }
    const data = await skyoneAction('toggle-user', payload);
    if (data) {
      setMessage({ type: 'success', text: currentAtivo ? 'Usuario desactivado' : 'Usuario activado' });
      setSkyModal(null);
      if (skySelectedClient) skySelectClient(skySelectedClient.cli_uuid);
    }
  };

  const formatSize = (mb) => {
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb} MB`;
  };

  const getBadgeClass = (state) => {
    if (state === 'running') return 'badge badge-running';
    if (state === 'stopped') return 'badge badge-stopped';
    return 'badge badge-pending';
  };

  const getStateLabel = (state) => {
    const labels = { running: 'Activo', stopped: 'Detenido', pending: 'Iniciando', stopping: 'Deteniendo' };
    return labels[state] || state;
  };

  const getWsBadge = (state) => {
    if (state === 'CONNECTED') return 'badge badge-running';
    if (state === 'DISCONNECTED') return 'badge badge-stopped';
    return 'badge badge-pending';
  };

  const getWsLabel = (state) => {
    const labels = { CONNECTED: 'Conectado', DISCONNECTED: 'Desconectado', UNKNOWN: 'Sin datos', AVAILABLE: 'Disponible', STOPPED: 'Detenido' };
    return labels[state] || state;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleString('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return ts;
    }
  };

  if (!user) {
    return (
      <div className="loading-screen">
        <div>
          <div className="spinner"></div>
          <p>Iniciando sesion...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header className="app-header">
        <div className="app-header-left">
          <h1>Siimed</h1>
          <span>Panel de Infraestructura</span>
        </div>
        <div className="app-header-right">
          <div className="user-info">
            <div>{user.username}</div>
          </div>
          <button className="btn-logout" onClick={() => Auth.signOut()}>
            Cerrar sesion
          </button>
        </div>
      </header>

      <div className="app-container">
        {functions.length > 0 && (
          <div className="cards-grid">
            {/* Quien solo administra servidores tambien necesita la tarjeta para entrar */}
            {(functions.includes('GestionServidores') && !functions.includes('ConsultaServidores')
              ? [...functions, 'ConsultaServidores']
              : functions
            ).filter(fn => !HIDDEN_FUNCTIONS.has(fn)).map(fn => {
              const config = FUNCTION_CONFIG[fn] || {
                label: fn, description: '', icon: '\u2699', btnClass: 'btn btn-info',
              };
              return (
                <div className="card" key={fn}>
                  <div className="card-icon">{config.icon}</div>
                  <h3>{config.label}</h3>
                  <p>{config.description}</p>
                  <button
                    className={config.btnClass}
                    onClick={() => {
                      if (fn === 'ConsultaArchivos') {
                        // Gerencia-Tecnologia uses portal directly; everyone else goes to internal IIS
                        if (userGroups.includes('Gerencia-Tecnologia')) {
                          executeFunction(fn, { action: 'list-folders' });
                        } else {
                          window.open(IIS_ARCHIVOS_URL, '_blank', 'noopener,noreferrer');
                        }
                      } else if (fn === 'ConsultaSetup') {
                        executeFunction(fn, { action: 'list-builds' });
                      } else if (fn === 'GestionSkyOne') {
                        skyLoadClients();
                      } else if (fn === 'ConsultaServidores') {
                        clearPanels();
                        setMessage(null);
                        setQsOpen(true);
                      } else {
                        executeFunction(fn);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Procesando...' : 'Ejecutar'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {message && (
          <div className={`message-box ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
            {message.text}
          </div>
        )}

        {/* Consultas sobre Servidores */}
        {qsOpen && (
          <QueryServers
            apiBase={API_BASE}
            getToken={getToken}
            canQuery={functions.includes('ConsultaServidores')}
            canAdmin={functions.includes('GestionServidores')}
            onClose={() => setQsOpen(false)}
          />
        )}

        {/* EC2 Instances */}
        {instances && (() => {
          const { pageItems, currentPage, totalPages, filtered } = filterAndPaginate(instances, 'ec2', inst => [inst.name, getStateLabel(inst.state)]);
          return (
          <div className="result-panel">
            <h3>Estado de Servidores</h3>
            {renderGridControls('ec2', filtered.length, currentPage, totalPages)}
            <table className="instances-table">
              <thead><tr><th>Servidor</th><th>Estado</th></tr></thead>
              <tbody>
                {pageItems.map(inst => (
                  <tr key={inst.name}>
                    <td>{inst.name}</td>
                    <td><span className={getBadgeClass(inst.state)}>{getStateLabel(inst.state)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}

        {/* WorkSpaces */}
        {wsData && (
          <div className="result-panel">
            <h3>{wsData.isMonitor ? 'Estado de Todos los WorkSpaces' : 'Mi WorkSpace'}</h3>
            {wsData.isMonitor && <p className="monitor-badge">Vista Monitor</p>}
            {(() => {
              const { pageItems, currentPage, totalPages, filtered } = filterAndPaginate(wsData.workspaces, 'ws', ws => [ws.adUser, getWsLabel(ws.wsState), getWsLabel(ws.connectionState), ws.ipAddress]);
              return (<>
                {renderGridControls('ws', filtered.length, currentPage, totalPages)}
                <table className="instances-table">
                  <thead><tr><th>Usuario</th><th>Estado WS</th><th>Conexion</th><th>Ultima conexion</th><th>IP</th></tr></thead>
                  <tbody>
                    {pageItems.map(ws => (
                      <tr key={ws.workspaceId}>
                        <td>{ws.adUser}</td>
                        <td><span className={getBadgeClass(ws.wsState === 'AVAILABLE' ? 'running' : 'stopped')}>{getWsLabel(ws.wsState)}</span></td>
                        <td><span className={getWsBadge(ws.connectionState)}>{getWsLabel(ws.connectionState)}</span></td>
                        <td>{formatTimestamp(ws.lastConnection)}</td>
                        <td>{ws.ipAddress || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>);
            })()}
            {wsData.history && wsData.history.length > 0 && (() => {
              const { pageItems, currentPage, totalPages, filtered } = filterAndPaginate(wsData.history, 'wsHistory', h => [h.adUser, formatTimestamp(h.timestamp), getWsLabel(h.event), h.ipAddress]);
              return (
              <div style={{marginTop: '24px'}}>
                <h3>Historial de Conexiones (ultimos 30 dias)</h3>
                {renderGridControls('wsHistory', filtered.length, currentPage, totalPages)}
                <table className="instances-table">
                  <thead><tr>{wsData.isMonitor && <th>Usuario</th>}<th>Fecha/Hora</th><th>Evento</th><th>IP</th></tr></thead>
                  <tbody>
                    {pageItems.map((h, i) => (
                      <tr key={i}>
                        {wsData.isMonitor && <td>{h.adUser}</td>}
                        <td>{formatTimestamp(h.timestamp)}</td>
                        <td><span className={getWsBadge(h.event)}>{getWsLabel(h.event)}</span></td>
                        <td>{h.ipAddress || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              );
            })()}

            {/* Usage Chart */}
            {wsData.usageChart && wsData.usageChart.labels && wsData.usageChart.labels.length > 0 && (
              <div style={{marginTop: '24px'}}>
                <h3>Horas de Uso por Dia</h3>
                <div className="chart-container">
                  <Bar
                    data={{
                      labels: wsData.usageChart.labels.map(d => {
                        const parts = d.split('-');
                        return `${parts[2]}/${parts[1]}`;
                      }),
                      datasets: wsData.usageChart.datasets.map((ds, i) => ({
                        label: ds.user,
                        data: ds.data,
                        backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                        borderRadius: 3,
                      }))
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'bottom', labels: { boxWidth: 12, padding: 15 } },
                        title: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)} horas`
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 14,
                          title: { display: true, text: 'Horas' },
                          ticks: { stepSize: 2 }
                        },
                        x: {
                          title: { display: true, text: 'Fecha' }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Setup Browser */}
        {setupData && !setupData.files && (
          <div className="result-panel">
            <h3>Setup - Builds SIIMED</h3>

            {/* Setup Diario */}
            {setupData.setupDiario && setupData.setupDiario.length > 0 && (
              <div style={{marginBottom: '24px'}}>
                <h4 style={{color: '#1b3a6b'}}>Setup Diario (version sin cambio)</h4>
                <div className="folder-grid">
                  {setupData.setupDiario.map(b => (
                    <div className="folder-item" key={b.name} style={{position:'relative'}} onClick={() => browseBuild(b, 'setup')}>
                      {functions.includes('GestionSetup') && (
                        <button
                          title={`Eliminar build ${b.name}`}
                          onClick={(e) => { e.stopPropagation(); openDeleteBuildModal(b, 'setup'); }}
                          style={{position:'absolute',top:4,right:6,background:'transparent',border:'none',color:'#d13212',fontSize:18,cursor:'pointer',padding:'2px 6px'}}
                        >{'\uD83D\uDDD1'}</button>
                      )}
                      <div className="folder-icon">{'\uD83D\uDCE6'}</div>
                      <div className="folder-name">{b.name}</div>
                      <div className="folder-tooltip">Diario</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actualizadores */}
            {setupData.actualizadores && setupData.actualizadores.length > 0 && (
              <div>
                <h4 style={{color: '#7ac143'}}>Actualizadores (version nueva)</h4>
                <div className="folder-grid">
                  {setupData.actualizadores.map(b => (
                    <div className="folder-item" key={b.name} style={{position:'relative'}} onClick={() => browseBuild(b, 'actualizador')}>
                      {functions.includes('GestionSetup') && (
                        <button
                          title={`Eliminar build ${b.name}`}
                          onClick={(e) => { e.stopPropagation(); openDeleteBuildModal(b, 'actualizador'); }}
                          style={{position:'absolute',top:4,right:6,background:'transparent',border:'none',color:'#d13212',fontSize:18,cursor:'pointer',padding:'2px 6px'}}
                        >{'\uD83D\uDDD1'}</button>
                      )}
                      <div className="folder-icon">{'\u2B06'}</div>
                      <div className="folder-name">{b.name}</div>
                      <div className="folder-tooltip">Actualizador</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!setupData.setupDiario || setupData.setupDiario.length === 0) && (!setupData.actualizadores || setupData.actualizadores.length === 0) && (
              <p>No hay builds disponibles.</p>
            )}
          </div>
        )}

        {/* Setup Files */}
        {setupData && setupData.files && setupPath.length > 0 && (() => {
          const { pageItems, currentPage, totalPages, filtered } = filterAndPaginate(setupData.files, 'setupFiles', f => [f.name]);
          return (
          <div className="result-panel">
            <div className="file-browser-header">
              <h3>Setup - {setupPath[0].source === 'actualizador' ? 'Actualizador' : 'Setup Diario'}</h3>
              <div className="breadcrumb">
                <span className="breadcrumb-link" onClick={goBackToBuilds}>Builds</span>
                <span> / {setupPath[0].name}</span>
              </div>
            </div>
            {functions.includes('GestionSetup') && (
              <div style={{margin:'8px 0 12px', display:'flex', gap:'8px', alignItems:'center'}}>
                <label className="btn btn-start btn-sm" style={{cursor:'pointer', margin:0}}>
                  {'⬆'} Subir archivo a este build
                  <input
                    type="file"
                    style={{display:'none'}}
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) { uploadSetupFile(setupPath[0], setupPath[0].source, f); e.target.value = ''; } }}
                  />
                </label>
                <span style={{fontSize:12, color:'#6b7280'}}>Se sube directo a S3 (sin limite practico de tamano)</span>
              </div>
            )}
            {renderGridControls('setupFiles', filtered.length, currentPage, totalPages)}
            <table className="instances-table">
              <thead><tr><th>Archivo</th><th>Tamano</th><th>Fecha</th><th>Descargar</th>{functions.includes('GestionSetup') && <th>Eliminar</th>}</tr></thead>
              <tbody>
                {pageItems.map(f => (
                  <tr key={f.name}>
                    <td>{f.name}</td>
                    <td>{formatSize(f.size)}</td>
                    <td>{formatTimestamp(f.lastModified)}</td>
                    <td><button className="btn btn-start btn-sm" onClick={() => downloadSetupFile(f.key, f.name)}>Descargar</button></td>
                    {functions.includes('GestionSetup') && (
                      <td><button className="btn btn-stop btn-sm" onClick={() => openDeleteFileModal(f.key, f.name)}>Eliminar</button></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}

        {/* S3 File Browser */}
        {fileData && (
          <div className="result-panel">
            <div className="file-browser-header">
              <h3>Archivos — Backups Clientes</h3>
              {filePath.length > 0 && (
                <div className="breadcrumb">
                  <span className="breadcrumb-link" onClick={goBackToFolders}>Carpetas</span>
                  {filePath[0] && <span> / <span className={filePath.length > 1 ? 'breadcrumb-link' : ''} onClick={filePath.length > 1 ? goBackToDates : undefined}>{filePath[0].tooltip || filePath[0].name}</span></span>}
                  {filePath[1] && <span> / {filePath[1].name}</span>}
                </div>
              )}
            </div>

            {/* Folders view */}
            {fileData.folders && (
              <div className="folder-grid">
                {fileData.folders.map(f => (
                  <div className="folder-item" key={f.name} onClick={() => browseFolder(f)}>
                    <div className="folder-icon">{'\uD83D\uDCC1'}</div>
                    <div className="folder-name">{f.name}</div>
                    {f.tooltip && <div className="folder-tooltip">{f.tooltip}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Dates view */}
            {fileData.dates && (
              <div className="folder-grid">
                {fileData.dates.map(d => (
                  <div className="folder-item date-item" key={d.name} onClick={() => browseDate(d)}>
                    <div className="folder-icon">{'\uD83D\uDCC5'}</div>
                    <div className="folder-name">{d.name}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Files view */}
            {fileData.files && (() => {
              const { pageItems, currentPage, totalPages, filtered } = filterAndPaginate(fileData.files, 's3Files', f => [f.name]);
              return (<>
                {renderGridControls('s3Files', filtered.length, currentPage, totalPages)}
                <table className="instances-table">
                  <thead><tr><th>Archivo</th><th>Tamano</th><th>Fecha</th><th>Descargar</th></tr></thead>
                  <tbody>
                    {pageItems.map(f => (
                      <tr key={f.name}>
                        <td>{f.name}</td>
                        <td>{formatSize(f.size)}</td>
                        <td>{formatTimestamp(f.lastModified)}</td>
                        <td><button className="btn btn-start btn-sm" onClick={() => downloadFile(f.key, f.name)}>Descargar</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>);
            })()}
          </div>
        )}

        {/* SkyOne - Client Selector */}
        {skyView === 'clients' && skyClients && (() => {
          const { pageItems, currentPage, totalPages, filtered } = filterAndPaginate(skyClients, 'skyClients', c => [c.nome, String(c.active_sessions_count)]);
          return (
          <div className="result-panel">
            <h3>Gestion SkyOne — Seleccionar Cliente</h3>
            <div className="skyone-export-bar">
              <button className="btn btn-info btn-sm" disabled={skyExporting} onClick={() => skyExportUsers('all')}>
                {skyExporting ? 'Exportando…' : 'Exportar usuarios (xlsx)'}
              </button>
              <label className="skyone-export-check">
                <input type="checkbox" checked={skyExcludeSiimed} onChange={e => setSkyExcludeSiimed(e.target.checked)} />
                <span>Excluir usuarios siimed.com.co</span>
              </label>
            </div>
            {renderGridControls('skyClients', filtered.length, currentPage, totalPages)}
            <table className="instances-table">
              <thead><tr><th>Cliente</th><th>Sesiones Activas</th><th>Acción</th></tr></thead>
              <tbody>
                {pageItems.map(c => (
                  <tr key={c.cli_uuid}>
                    <td>{c.nome}</td>
                    <td>{c.active_sessions_count}</td>
                    <td><button className="btn btn-info btn-sm" onClick={() => skySelectClient(c.cli_uuid)}>Ver</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}

        {/* SkyOne - Client Detail + Users */}
        {skyView === 'detail' && skySelectedClient && (
          <div className="result-panel">
            <div className="file-browser-header">
              <h3>SkyOne — {skySelectedClient.nome}</h3>
              <div className="breadcrumb">
                <span className="breadcrumb-link" onClick={skyLoadClients}>Clientes</span>
                <span> / {skySelectedClient.nome}</span>
              </div>
            </div>

            <div className="skyone-client-info">
              <p><strong>UUID:</strong> {skySelectedClient.cli_uuid}</p>
              <p><strong>Email Admin:</strong> {skySelectedClient.email_admin}</p>
              <p><strong>Grupo AD:</strong> {skySelectedClient.grupo_seguranca}</p>
              <p><strong>Mantenimiento:</strong> <span className={skySelectedClient.in_maintenance ? 'badge badge-stopped' : 'badge badge-running'}>{skySelectedClient.in_maintenance ? 'Si' : 'No'}</span></p>
              <p><strong>Sesiones activas:</strong> {skySelectedClient.active_sessions_count}</p>
              {skySelectedClient.external_client_code && <p><strong>Cod. externo:</strong> {skySelectedClient.external_client_code}</p>}
            </div>

            <div style={{margin: '16px 0', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
              <button className="btn btn-start" onClick={() => {
                setSkyFormData({ cli_uuid: skySelectedClient.cli_uuid, ativo: true, send_activation_email: false, external_ad: false });
                setSkyModal({ type: 'create' });
              }}>+ Crear Usuario</button>
              <button className="btn btn-info btn-sm" disabled={skyExporting} onClick={() => skyExportUsers('client')}>
                {skyExporting ? 'Exportando…' : 'Exportar usuarios (xlsx)'}
              </button>
              <label className="skyone-export-check">
                <input type="checkbox" checked={skyExcludeSiimed} onChange={e => setSkyExcludeSiimed(e.target.checked)} />
                <span>Excluir usuarios siimed.com.co</span>
              </label>
            </div>

            {(() => {
              const users = skySelectedClient.users || [];
              const { pageItems, currentPage, totalPages, filtered } = filterAndPaginate(users, 'skyUsers', u => [u.nome, u.sobrenome, u.email, u.ativo ? 'Activo' : 'Inactivo']);
              return (<>
                <h4 style={{color: '#1b3a6b', marginBottom: '12px'}}>Usuarios ({users.length})</h4>
                {renderGridControls('skyUsers', filtered.length, currentPage, totalPages)}
                <table className="instances-table">
                  <thead><tr><th>Nombre</th><th>Email</th><th>Estado</th><th>AD Externo</th><th>Acciones</th></tr></thead>
                  <tbody>
                    {pageItems.map(u => (
                      <tr key={u.usr_uuid}>
                        <td>{u.nome} {u.sobrenome || ''}</td>
                        <td>{u.email}</td>
                        <td><span className={u.ativo ? 'badge badge-running' : 'badge badge-stopped'}>{u.ativo ? 'Activo' : 'Inactivo'}</span></td>
                        <td>{u.external_ad ? 'Si' : 'No'}</td>
                        <td>
                          <div className="skyone-actions">
                            <button className="btn btn-info btn-sm" onClick={() => skyGetUserDetail(u.usr_uuid)} title="Ver datos extendidos">Ver</button>
                            <button className="btn btn-info btn-sm" onClick={() => { setSkyFormData({nome: u.nome, sobrenome: u.sobrenome || '', ativo: u.ativo, description: ''}); setSkyModal({type: 'edit', user: u}); }} title="Editar">Editar</button>
                            <button className={`btn btn-sm ${u.ativo ? 'btn-stop' : 'btn-start'}`} onClick={() => { setSkyFormData({sendActivationEmail: !u.ativo}); setSkyModal({type: 'toggle', user: u}); }} title={u.ativo ? 'Desactivar' : 'Activar'}>{u.ativo ? 'Desactivar' : 'Activar'}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>);
            })()}
          </div>
        )}

        {/* Setup Confirmation Modal — eliminar archivo / eliminar build */}
        {setupModal && (
          <div className="skyone-overlay">
            <div className="skyone-modal" style={{maxWidth:'480px'}}>

              {setupModal.type === 'delete-file' && (
                <div>
                  <h3 style={{color:'#d13212'}}>{'🗑'} Eliminar archivo</h3>
                  <p style={{fontSize:14, lineHeight:1.5, margin:'12px 0'}}>
                    ¿Confirmas la eliminacion del archivo?
                  </p>
                  <div style={{background:'#f4f6f9', border:'1px solid #e5e7eb', borderRadius:6, padding:'10px 14px', fontFamily:'Consolas,Monaco,monospace', fontSize:13, wordBreak:'break-all', margin:'8px 0 16px'}}>
                    {setupModal.fileName}
                  </div>
                  <p style={{fontSize:12, color:'#d13212', margin:'0 0 4px'}}>
                    <strong>⚠</strong> Esta accion no se puede deshacer.
                  </p>
                  <div className="skyone-modal-buttons">
                    <button className="btn btn-stop" onClick={confirmDeleteSetupFile}>{'🗑'} Eliminar</button>
                    <button className="btn btn-info" onClick={() => setSetupModal(null)}>Cancelar</button>
                  </div>
                </div>
              )}

              {setupModal.type === 'delete-build' && (
                <div>
                  <h3 style={{color:'#d13212'}}>{'🗑'} Eliminar build completo</h3>
                  <p style={{fontSize:14, lineHeight:1.5, margin:'12px 0 8px'}}>
                    Vas a eliminar el build <strong>y TODOS sus archivos</strong>. Esta accion no se puede deshacer.
                  </p>
                  <div style={{background:'#fef3c7', borderLeft:'4px solid #f59e0b', borderRadius:4, padding:'10px 14px', margin:'8px 0 16px'}}>
                    <div style={{fontSize:11, color:'#92400e', textTransform:'uppercase', letterSpacing:1, fontWeight:600, marginBottom:4}}>Build a eliminar</div>
                    <div style={{fontFamily:'Consolas,Monaco,monospace', fontSize:16, fontWeight:700, color:'#78350f'}}>{setupModal.build.name}</div>
                  </div>
                  <div className="skyone-form-field">
                    <label>Escribe el nombre exacto del build para confirmar:</label>
                    <input
                      type="text"
                      value={setupModalInput}
                      onChange={(e) => setSetupModalInput(e.target.value)}
                      placeholder={setupModal.build.name}
                      autoFocus
                    />
                  </div>
                  <div className="skyone-modal-buttons">
                    <button
                      className="btn btn-stop"
                      disabled={setupModalInput.trim() !== setupModal.build.name}
                      onClick={confirmDeleteSetupBuild}
                    >{'🗑'} Eliminar build</button>
                    <button className="btn btn-info" onClick={() => setSetupModal(null)}>Cancelar</button>
                  </div>
                </div>
              )}

            </div>
          </div>
        )}

        {/* SkyOne Modal */}
        {skyModal && (
          <div className="skyone-overlay">
            <div className="skyone-modal">
              {/* VIEW modal */}
              {skyModal.type === 'view' && skyModal.user && (
                <div>
                  <h3>Datos Extendidos del Usuario</h3>
                  <table className="instances-table">
                    <tbody>
                      {Object.entries(skyModal.user).filter(([k]) => k !== 'clients').map(([k, v]) => (
                        <tr key={k}><td><strong>{skyLabel(k)}</strong></td><td>{typeof v === 'boolean' ? (v ? 'Si' : 'No') : String(v ?? '')}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  {skyModal.user.clients && skyModal.user.clients.length > 0 && (
                    <div style={{marginTop: '16px'}}>
                      <h4 style={{color: '#1b3a6b'}}>Clientes vinculados</h4>
                      <table className="instances-table">
                        <thead><tr><th>Cliente</th><th>UUID</th></tr></thead>
                        <tbody>
                          {skyModal.user.clients.map(c => (
                            <tr key={c.cli_uuid}><td>{c.nome}</td><td>{c.cli_uuid}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <button className="btn btn-info" style={{marginTop: '16px'}} onClick={() => setSkyModal(null)}>Cerrar</button>
                </div>
              )}

              {/* CREATE modal */}
              {skyModal.type === 'create' && (
                <div>
                  <h3>Crear Usuario SkyOne</h3>
                  {['nome', 'sobrenome', 'email', 'password', 'fullname', 'description'].map(field => {
                    const required = ['nome', 'sobrenome', 'email', 'fullname'].includes(field);
                    return (
                      <div key={field} className="skyone-form-field">
                        <label>{skyLabel(field)}{required ? ' *' : ''}</label>
                        <input type={field === 'password' ? 'password' : 'text'} value={skyFormData[field] || ''} onChange={e => setSkyFormData({...skyFormData, [field]: e.target.value})} />
                      </div>
                    );
                  })}
                  {['ativo', 'external_ad', 'send_activation_email'].map(field => (
                    <div key={field} className="skyone-form-field skyone-checkbox">
                      <label><input type="checkbox" checked={!!skyFormData[field]} onChange={e => setSkyFormData({...skyFormData, [field]: e.target.checked})} /> {skyLabel(field)}</label>
                    </div>
                  ))}
                  <div className="skyone-modal-buttons">
                    <button className="btn btn-start" onClick={skyCreateUser} disabled={loading || !skyFormData.nome || !skyFormData.sobrenome || !skyFormData.email || !skyFormData.fullname}>Crear</button>
                    <button className="btn btn-info" onClick={() => setSkyModal(null)}>Cancelar</button>
                  </div>
                </div>
              )}

              {/* EDIT modal */}
              {skyModal.type === 'edit' && (
                <div>
                  <h3>Editar Usuario: {skyModal.user.email}</h3>
                  {['nome', 'sobrenome', 'description'].map(field => {
                    const required = ['nome', 'sobrenome'].includes(field);
                    return (
                      <div key={field} className="skyone-form-field">
                        <label>{skyLabel(field)}{required ? ' *' : ''}</label>
                        <input type="text" value={skyFormData[field] || ''} onChange={e => setSkyFormData({...skyFormData, [field]: e.target.value})} />
                      </div>
                    );
                  })}
                  <div className="skyone-form-field skyone-checkbox">
                    <label><input type="checkbox" checked={!!skyFormData.ativo} onChange={e => setSkyFormData({...skyFormData, ativo: e.target.checked})} /> {skyLabel('ativo')}</label>
                  </div>
                  <div className="skyone-modal-buttons">
                    <button className="btn btn-start" onClick={() => skyEditUser(skyModal.user.usr_uuid)} disabled={loading || !skyFormData.nome || !skyFormData.sobrenome}>Guardar</button>
                    <button className="btn btn-info" onClick={() => setSkyModal(null)}>Cancelar</button>
                  </div>
                </div>
              )}

              {/* TOGGLE confirmation */}
              {skyModal.type === 'toggle' && (
                <div>
                  <h3>{skyModal.user.ativo ? 'Desactivar' : 'Activar'} Usuario</h3>
                  <p>Confirma que desea {skyModal.user.ativo ? 'desactivar' : 'activar'} a <strong>{skyModal.user.email}</strong>?</p>
                  {!skyModal.user.ativo && (
                    <div className="skyone-form-field skyone-checkbox">
                      <label>
                        <input type="checkbox" checked={!!skyFormData.sendActivationEmail} onChange={e => setSkyFormData({...skyFormData, sendActivationEmail: e.target.checked})} />
                        {' '}Enviar correo de activación al usuario
                      </label>
                    </div>
                  )}
                  <div className="skyone-modal-buttons">
                    <button className={`btn ${skyModal.user.ativo ? 'btn-stop' : 'btn-start'}`} onClick={() => skyToggleUser(skyModal.user.usr_uuid, skyModal.user.ativo, !!skyFormData.sendActivationEmail)} disabled={loading}>{skyModal.user.ativo ? 'Desactivar' : 'Activar'}</button>
                    <button className="btn btn-info" onClick={() => setSkyModal(null)}>Cancelar</button>
                  </div>
                </div>
              )}

            </div>
          </div>
        )}

        {loading && (
          <div className="loading-overlay">
            <div className="loading-box">
              <div className="spinner"></div>
              <p>Ejecutando...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
